import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { departmentsAPI, employeesAPI, importAPI } from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import SearchableSelect from '../components/ui/SearchableSelect';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import EmptyState from '../components/ui/EmptyState';
import Alert from '../components/ui/Alert';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useToast } from '../context/ToastContext';

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const Departments = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Linkul „Departamente" din sidebar pentru rolurile interne (COMPANY_ADMIN, DIRECTOR_GENERAL,
  // EMPLOYEE/CLM/HR) nu trimite `?companyId`. Fallback la firma proprie a userului, ca aceste
  // roluri să vadă departamentele firmei lor în loc de a fi redirecționate la /companies
  // (rută la care n-au acces → ajungeau pe dashboard). Rolurile SSM trimit `?companyId` explicit.
  const companyId = searchParams.get('companyId') || user?.companyId || null;

  const [departments, setDepartments] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({ name: '', managerId: '', type: '', isHR: false });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete
  const [deletingDept, setDeletingDept] = useState(null);

  // Expand state
  const [expandedDeptId, setExpandedDeptId] = useState(null);
  const [expandedDeptData, setExpandedDeptData] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);

  // Unassigned employees
  const [unassignedEmployees, setUnassignedEmployees] = useState([]);
  const [expandUnassigned, setExpandUnassigned] = useState(false);

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importDeptId, setImportDeptId] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchDepartments = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const [deptRes, empRes, unassignedRes] = await Promise.all([
        departmentsAPI.getAll(companyId),
        employeesAPI.getAll({ companyId }),
        employeesAPI.getAll({ companyId, unassigned: 'true' }),
      ]);
      setDepartments(deptRes.data);
      setUnassignedEmployees(unassignedRes.data);

      // Toți angajații activi din companie pot fi responsabili de departament
      const availableManagers = empRes.data
        .filter((emp) => emp.user.active !== false)
        .map((emp) => ({
          value: emp.user.id,
          label: `${emp.user.firstName} ${emp.user.lastName}`,
          subtitle: `${emp.user.email}${emp.jobTitle ? ` · ${emp.jobTitle}` : ''}`,
        }));
      setManagers(availableManagers);

      // Obține numele companiei din primul response sau separat
      if (deptRes.data.length > 0 && deptRes.data[0].company) {
        setCompanyName(deptRes.data[0].company.name);
      } else if (empRes.data.length > 0 && empRes.data[0].company) {
        setCompanyName(empRes.data[0].company.name);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('departments.loadError'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      // Fără context de firmă (ex: SUPER_ADMIN care a accesat /departments fără ?companyId):
      // rolurile SSM aleg firma din /companies, restul revin pe dashboard.
      const ssmRoles = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'];
      navigate(ssmRoles.includes(user?.role) ? '/companies' : '/dashboard');
      return;
    }
    fetchDepartments();
  }, [companyId, fetchDepartments, navigate, user?.role]);

  const openCreateModal = () => {
    setEditingDept(null);
    setFormData({ name: '', managerId: '', type: '' });
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      managerId: dept.managerId || '',
      type: dept.type || '',
      isHR: dept.isHR === true,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError(t('departments.nameRequired'));
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (editingDept) {
        await departmentsAPI.update(editingDept.id, {
          name: formData.name.trim(),
          managerId: formData.managerId || null,
          type: formData.type.trim() || null,
          isHR: !!formData.isHR,
        });
      } else {
        await departmentsAPI.create({
          name: formData.name.trim(),
          companyId,
          managerId: formData.managerId || null,
          type: formData.type.trim() || null,
          isHR: !!formData.isHR,
        });
      }
      setShowModal(false);
      fetchDepartments();
    } catch (err) {
      setFormError(err.response?.data?.error || t('departments.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingDept) return;
    try {
      await departmentsAPI.delete(deletingDept.id);
      setDeletingDept(null);
      fetchDepartments();
    } catch (err) {
      showToast(err.response?.data?.error || t('departments.deleteErrorToast'), 'error');
      setDeletingDept(null);
    }
  };

  const openImportModal = (deptId = null) => {
    setImportDeptId(deptId);
    setImportFile(null);
    setImportResult(null);
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      if (importDeptId) {
        fd.append('departmentId', importDeptId);
      }
      fd.append('companyId', companyId);

      const res = await importAPI.uploadEmployees(fd);
      setImportResult(res.data);
      fetchDepartments();
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || t('departments.importError') });
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await importAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_angajati.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(t('departments.templateDownloadError'), 'error');
    }
  };

  // Rolurile SSM au acces la lista de firme (/companies) și la detaliile firmei; rolurile interne
  // au acces doar la propria firmă (/companies/:id). Construim breadcrumb-ul în consecință ca să
  // nu generăm linkuri moarte.
  const isSSMRole = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role);

  const toggleExpand = async (deptId) => {
    if (expandedDeptId === deptId) {
      setExpandedDeptId(null);
      setExpandedDeptData(null);
      return;
    }
    setExpandedDeptId(deptId);
    setExpandedDeptData(null);
    setExpandLoading(true);
    try {
      const res = await departmentsAPI.getById(deptId);
      setExpandedDeptData(res.data);
    } catch (err) {
      console.error('Error loading department details:', err);
    } finally {
      setExpandLoading(false);
    }
  };

  if (loading) {
    return <Spinner size="lg" label={t('departments.loading')} />;
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={
          companyId
            ? [
                // Linkul „Firme" doar pentru rolurile SSM (rolurile interne n-au acces la /companies).
                ...(isSSMRole ? [{ label: t('nav.companies'), href: '/companies' }] : []),
                {
                  label: companyName || t('departments.companyFallback'),
                  href: `/companies/${companyId}`,
                },
                { label: t('departments.breadcrumb') },
              ]
            : [{ label: t('departments.breadcrumb') }]
        }
      />
      <PageHeader
        title={`${t('departments.title')}${companyName ? ` — ${companyName}` : ''}`}
        subtitle={t('departments.countSubtitle', { count: departments.length })}
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/companies/${companyId}`)}>
              <ArrowLeftIcon /> {t('common.back')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => openImportModal()}>
              <UploadIcon /> {t('departments.importXls')}
            </Button>
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              <PlusIcon /> {t('departments.newDept')}
            </Button>
          </>
        }
      />

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {departments.length === 0 ? (
        <EmptyState
          icon={<UsersIcon />}
          title={t('departments.emptyTitle')}
          description={t('departments.emptyDesc')}
          actionLabel={t('departments.addDept')}
          onAction={openCreateModal}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map((dept) => {
            const isExpanded = expandedDeptId === dept.id;
            return (
              <Card
                key={dept.id}
                className={`relative cursor-pointer transition-all ${isExpanded ? 'col-span-1 md:col-span-2 lg:col-span-3 ring-2 ring-primary-200 dark:ring-primary-700' : 'hover:shadow-md'}`}
                onClick={() => toggleExpand(dept.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {dept.name}
                    </h3>
                    {dept.type && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded">
                        {dept.type}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImportModal(dept.id);
                      }}
                      className="p-1.5 text-slate-400 dark:text-slate-400 hover:text-primary-600 transition-colors"
                      title={t('departments.importEmployeesXls')}
                    >
                      <UploadIcon />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(dept);
                      }}
                      className="p-1.5 text-slate-400 dark:text-slate-400 hover:text-primary-600 transition-colors"
                      title={t('common.edit')}
                    >
                      <PencilIcon />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingDept(dept);
                      }}
                      className="p-1.5 text-slate-400 dark:text-slate-400 hover:text-danger-600 transition-colors"
                      title={t('common.delete')}
                    >
                      <TrashIcon />
                    </button>
                    <div
                      className={`p-1.5 text-slate-400 dark:text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <UsersIcon />
                    <span className="text-slate-600 dark:text-slate-300">
                      {t('departments.employeesCount', { count: dept._count?.employees || 0 })}
                    </span>
                  </div>
                  {dept.manager ? (
                    <div className="flex items-center gap-2 text-sm">
                      <svg
                        className="w-5 h-5 text-slate-400 dark:text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span className="text-slate-600 dark:text-slate-300">
                        {t('departments.managerLabel', {
                          name: `${dept.manager.firstName} ${dept.manager.lastName}`,
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm">
                      <svg
                        className="w-5 h-5 text-warning-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span className="text-warning-600 dark:text-warning-300 font-medium">
                        {t('departments.noManager')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Expanded section */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                    {expandLoading ? (
                      <div className="flex justify-center py-4">
                        <Spinner size="sm" />
                      </div>
                    ) : expandedDeptData ? (
                      <div className="space-y-3">
                        {/* Manager/Responsabil */}
                        {expandedDeptData.manager && (
                          <div className="flex items-center gap-2 p-2 bg-primary-50 dark:bg-primary-950/50 rounded-lg">
                            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center text-primary-700 dark:text-primary-300 font-semibold text-xs">
                              {expandedDeptData.manager.firstName[0]}
                              {expandedDeptData.manager.lastName[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {expandedDeptData.manager.firstName}{' '}
                                {expandedDeptData.manager.lastName}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {expandedDeptData.manager.email}
                              </p>
                            </div>
                            <Badge color="blue">{t('departments.manager')}</Badge>
                          </div>
                        )}
                        {/* Employee list */}
                        {expandedDeptData.employees?.length > 0 ? (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                              {t('departments.employeesHeader', {
                                count: expandedDeptData.employees.length,
                              })}
                            </p>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {expandedDeptData.employees.map((emp) => (
                                <Link
                                  key={emp.id}
                                  to={`/employees/${emp.id}`}
                                  className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                                >
                                  <div className="w-6 h-6 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-600 dark:text-slate-300 font-medium text-xs flex-shrink-0">
                                    {emp.user.firstName[0]}
                                    {emp.user.lastName[0]}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-slate-900 dark:text-slate-100 hover:text-primary-600">
                                      {emp.user.firstName} {emp.user.lastName}
                                    </span>
                                    {emp.jobTitle && (
                                      <span className="text-slate-400 dark:text-slate-400 ml-1">
                                        · {emp.jobTitle}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-slate-400 dark:text-slate-400 truncate">
                                    {emp.user.email}
                                  </span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                            {t('departments.noEmployeesInDept')}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Card Nealocati — arata ca un departament */}
      {unassignedEmployees.length > 0 && (
        <Card
          className={`cursor-pointer transition-all border-2 border-dashed border-warning-300 dark:border-warning-700 bg-warning-50/50 dark:bg-warning-950/50 ${expandUnassigned ? 'ring-2 ring-warning-200 dark:ring-warning-700' : 'hover:shadow-md'}`}
          onClick={() => setExpandUnassigned(!expandUnassigned)}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-warning-800 dark:text-warning-300">
                {t('departments.unassigned')}
              </h3>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-warning-100 dark:bg-warning-950/50 text-warning-700 dark:text-warning-300 rounded">
                {t('departments.noDepartment')}
              </span>
            </div>
            <div
              className={`p-1.5 text-warning-500 dark:text-warning-300 transition-transform ${expandUnassigned ? 'rotate-180' : ''}`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <UsersIcon />
            <span className="text-warning-700 dark:text-warning-300">
              {t('departments.unassignedCount', { count: unassignedEmployees.length })}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm mt-2">
            <svg
              className="w-5 h-5 text-warning-500 dark:text-warning-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-warning-600 dark:text-warning-300 font-medium">
              {t('departments.needsAllocation')}
            </span>
          </div>

          {expandUnassigned && (
            <div className="mt-4 pt-4 border-t border-warning-200 dark:border-warning-700">
              <div className="space-y-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {t('departments.unassignedHeader', { count: unassignedEmployees.length })}
                </p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {unassignedEmployees.map((emp) => (
                    <Link
                      key={emp.id}
                      to={`/employees/${emp.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 p-2 rounded hover:bg-warning-100 dark:hover:bg-warning-950/50 text-sm"
                    >
                      <div className="w-6 h-6 bg-warning-100 dark:bg-warning-950/50 rounded-full flex items-center justify-center text-warning-700 dark:text-warning-300 font-medium text-xs flex-shrink-0">
                        {emp.user.firstName[0]}
                        {emp.user.lastName[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-slate-900 dark:text-slate-100 hover:text-primary-600">
                          {emp.user.firstName} {emp.user.lastName}
                        </span>
                        {emp.jobTitle && (
                          <span className="text-slate-400 dark:text-slate-400 ml-1">
                            · {emp.jobTitle}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 dark:text-slate-400 truncate">
                        {emp.user.email}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingDept ? t('departments.editDeptTitle') : t('departments.newDept')}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {editingDept ? t('common.save') : t('departments.create')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {formError && <Alert type="error">{formError}</Alert>}
          <Input
            label={t('departments.nameLabel')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('departments.namePlaceholder')}
            helper={
              formData.isHR && !formData.name.trim() ? t('departments.hrNameHelper') : undefined
            }
            error={formData.isHR && !formData.name.trim() ? ' ' : undefined}
          />
          <Input
            label={t('departments.typeLabel')}
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            placeholder={t('departments.typePlaceholder')}
            helper={t('departments.typeHelper')}
          />
          <SearchableSelect
            label={t('departments.managerSelectLabel')}
            value={formData.managerId}
            onChange={(val) => setFormData({ ...formData, managerId: val })}
            placeholder={t('departments.managerSearchPlaceholder')}
            options={managers}
          />
          {managers.length === 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('departments.noEmployeesInCompany')}
            </p>
          )}
          {/* Departament HR — toți angajații primesc automat permisiuni HR.
              La bifare: auto-populează „Tip departament" cu „HR" (dacă e gol) ca să nu fie nevoie să-l
              tastezi manual, plus avertizează dacă numele e încă gol. */}
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!formData.isHR}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  isHR: checked,
                  // La activare, dacă tipul e gol → completăm „HR" (păstrăm valoarea existentă altfel)
                  type: checked && !prev.type.trim() ? 'HR' : prev.type,
                }));
              }}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {t('departments.hrDeptLabel')}
              </span>
              <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {t('departments.hrDeptDesc')}
              </span>
            </span>
          </label>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmModal
        open={!!deletingDept}
        onClose={() => setDeletingDept(null)}
        onConfirm={handleDelete}
        title={t('departments.deleteDeptTitle')}
        message={
          deletingDept?._count?.employees > 0
            ? t('departments.deleteMsgWithEmployees', {
                name: deletingDept?.name,
                count: deletingDept?._count?.employees,
              })
            : t('departments.deleteMsgConfirm', { name: deletingDept?.name })
        }
        confirmLabel={t('common.delete')}
        variant="danger"
      />

      {/* Import Modal */}
      <Modal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={
          importDeptId ? t('departments.importInDeptTitle') : t('departments.importFromXlsTitle')
        }
        size="lg"
        footer={
          !importResult ? (
            <div className="flex justify-between items-center w-full">
              <Button variant="ghost" size="sm" onClick={handleDownloadTemplate}>
                <DownloadIcon /> {t('departments.downloadTemplate')}
              </Button>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  loading={importLoading}
                  disabled={!importFile}
                >
                  {t('departments.importAction')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button variant="primary" onClick={() => setShowImportModal(false)}>
                {t('common.close')}
              </Button>
            </div>
          )
        }
      >
        {!importResult ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {importDeptId ? t('departments.importDeptDesc') : t('departments.importGeneralDesc')}
            </p>
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="block w-full text-sm text-slate-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 dark:file:bg-primary-950/50 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100"
              />
            </div>
            <Alert type="info">{t('departments.importColumnsInfo')}</Alert>
          </div>
        ) : importResult.error ? (
          <Alert type="error">{importResult.error}</Alert>
        ) : (
          <div className="space-y-4">
            <Alert type="success">
              {t('departments.importDoneCount', { count: importResult.created })}
            </Alert>
            {importResult.skipped > 0 && (
              <Alert type="warning">
                {t('departments.importSkippedRows', { count: importResult.skipped })}
              </Alert>
            )}
            {importResult.errors > 0 && (
              <Alert type="error">
                {t('departments.importErrorsCount', { count: importResult.errors })}
              </Alert>
            )}
            {importResult.autoCreatedDepartments?.length > 0 && (
              <Alert type="info">
                {t('departments.autoCreatedDepts', {
                  list: importResult.autoCreatedDepartments.join(', '),
                })}
              </Alert>
            )}
            {importResult.warnings > 0 && (
              <Alert type="warning">
                {t('departments.importWarningsCount', { count: importResult.warnings })}
              </Alert>
            )}
            {importResult.details?.warnings?.length > 0 && (
              <div className="max-h-48 overflow-y-auto bg-warning-50 dark:bg-warning-950/50 border border-warning-200 dark:border-warning-700 rounded p-2">
                <p className="text-sm font-medium text-warning-800 dark:text-warning-300 mb-2">
                  {t('departments.warningsPerEmployee')}
                </p>
                {importResult.details.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-warning-700 dark:text-warning-300">
                    {w.message ||
                      t('departments.rowMissing', {
                        row: w.row,
                        fields: (w.missing || []).join(', '),
                      })}
                  </p>
                ))}
              </div>
            )}
            {importResult.details?.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('departments.errorDetails')}
                </p>
                {importResult.details.errors.map((err, i) => (
                  <p key={i} className="text-xs text-danger-600 dark:text-danger-300">
                    {t('departments.rowError', { row: err.row, error: err.error })}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Departments;
